import { useState, useEffect, useCallback } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';

export function formatAddressString({ address1, city, state, zip }: AutoCompleteValues) {
  if (address1 === '' || city === '' || state === '' || zip === '') {
    return '';
  }

  return `${address1}, ${city}, ${state} ${zip}`;
}

interface AddressBlob {
  street_number?: string;
  route?: string;
  locality?: string;
  sublocality_level_1?: string;
  administrative_area_level_1?: string;
  country?: string;
  postal_code?: string;
}

interface AutoCompleteValues {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

function getAddressComponent(place: google.maps.places.PlaceResult): AddressBlob {
  if (!place?.address_components) return {};

  return place.address_components.reduce((acc, cur) => {
    return {
      ...acc,
      [cur.types[0]]: cur.short_name,
    };
  }, {});
}

export function useGoogleAutocomplete(
  addressInputRef: React.RefObject<HTMLInputElement | null>,
  defaultValues: AutoCompleteValues
) {
  const places = useMapsLibrary('places');
  const [placeAutocomplete, setPlaceAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [place, setPlace] = useState<google.maps.places.PlaceResult | null>(null);

  const [address1, setAddress1] = useState<string>(defaultValues.address1);
  const [city, setCity] = useState<string>(defaultValues.city);
  const [state, setState] = useState<string>(defaultValues.state);
  const [zip, setZip] = useState<string>(defaultValues.zip);

  const clear = useCallback(() => {
    setAddress1('');
    setCity('');
    setState('');
    setZip('');
    setPlace(null);
  }, []);

  useEffect(() => {
    if (!places || !addressInputRef.current) {
      return;
    }

    const options = {
      fields: ['geometry', 'name', 'formatted_address', 'address_components'],
      componentRestrictions: { country: ['us', 'ca'] },
      types: ['address'],
    };

    setPlaceAutocomplete(new places.Autocomplete(addressInputRef.current, options));
  }, [addressInputRef, places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      const address = getAddressComponent(place);

      if (address.street_number === undefined) {
        setAddress1(address.route ?? '');
      } else {
        setAddress1(address.street_number + ' ' + address.route);
      }

      setCity(address.locality ?? address.sublocality_level_1 ?? '');
      setState(address.administrative_area_level_1 ?? '');
      setZip(address.postal_code ?? '');

      setPlace(place);
    });
  }, [placeAutocomplete]);

  return { place, address1, city, state, zip, clear };
}
